import { Canceler } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { FixedTab, RouteTabs, VariableTab } from '../../../components/RouteTabs';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';
import { ILink } from '../../../services/types';
import { replace } from '../../../services/utils';
import { RouteNames } from '../../config';
import LinkList from './List';
import LinkView from './View';
import { getLinks, deleteLink } from '../../../services/api/Link';

interface IProps {}

interface IParamProps {
  linkId?: string;
}

const Link = (props: IProps & RouteComponentProps): JSX.Element => {
  const path = RouteNames.Link;
  const match = useRouteMatch<IParamProps>(`${path}/v/:linkId`);

  const history = useHistory();
  const location = useLocation();
  const linkId = Number(match?.params?.linkId);

  const initialLinkIds = linkId ? [linkId] : [];
  const [editingLinkIds, setEditingLinkIds] = useState<number[]>(initialLinkIds);

  const [links, setLinks] = useState<ILink[]>();
  const [linksTotalCount, setLinksTotalCount] = useState<number>(0);

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const canceler = useRef<Canceler>(() => undefined);

  const handleError = useCallback(
    e => {
      snackbar.error(`An error has occurred. ${e.message}`);
    },
    [snackbar],
  );

  const fetchData = async (page: number, rowsPerPage: number) => {
    canceler.current();

    try {
      const [fetchPromise, cancel] = getLinks(page + 1, rowsPerPage);
      canceler.current = cancel;

      const result = await fetchPromise;

      setLinks(result.data.links);
      setLinksTotalCount(result.data.totalCount);
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  useEffect(() => {
    if (location.pathname === RouteNames.Link) {
      if (!links) {
        fetchData(0, 20);
      }
    }

    return () => canceler.current();
  }, [location.pathname, links]);

  const handleRowClick = useCallback(
    (link: ILink) => {
      if (!link || !link.id) return;

      if (!editingLinkIds.find(id => id === link.id)) {
        setEditingLinkIds([...editingLinkIds, link.id]);
      }
    },
    [editingLinkIds],
  );

  const handleDeleteClick = useCallback(
    (link: ILink) => {
      const onProceed = async () => {
        if (!links || !link.id) return;

        const idx = links.findIndex(l => l.id === link.id);

        try {
          backdrop.show();
          const result = await deleteLink(link.id);

          if (result.status === 200) {
            const cloned = [...links];
            cloned.splice(idx, 1);

            setLinks(cloned);

            snackbar.success('Deleted successfully.');
          } else {
            snackbar.error(`An error has occurred. ${result.statusText}`);
          }
        } catch (e) {
          console.error(e);
          snackbar.error(`An error has occurred. ${e.message}`);
        } finally {
          backdrop.hide();
        }
      };

      confirm.open({
        title: 'Delete',
        text: 'Are you sure you want to delete this link?',
        onProceed,
      });
    },
    [backdrop, confirm, snackbar, links],
  );

  const handleCreate = useCallback(
    data => {
      if (links) {
        setLinks([data, ...links]);
      } else {
        fetchData(0, 20);
      }

      history.push(RouteNames.Link);

      snackbar.success('Saved successfully.');
    },
    [history, snackbar, links],
  );

  const handleTabClose = useCallback(
    linkId => {
      setEditingLinkIds(editingLinkIds.filter(id => id !== linkId));
    },
    [editingLinkIds],
  );

  const getTabLabel = useCallback(
    linkId => {
      const link = links?.find(l => l.id === linkId);
      const label = link ? link.title : `VIEW ${linkId}`;

      return label;
    },
    [links],
  );

  const handleEdit = useCallback(
    link => {
      if (links) {
        const replaced = replace(links, l => l.id === link.id, link);
        setLinks(replaced);
      }

      snackbar.success('Saved changes successfully.');
    },
    [links, snackbar],
  );

  const handleRequestFetch = useCallback((page, rowsPerPage) => fetchData(page, rowsPerPage), []);

  const getTabUri = useCallback(linkId => `v/${linkId}`, []);

  const keyExtractor = useCallback(linkId => `tab-floating-${linkId}`, []);

  const variableTabRenderer = useCallback(linkId => <LinkView linkId={linkId} onCreated={handleEdit} onError={handleError} />, [
    handleEdit,
    handleError,
  ]);

  return (
    <RouteTabs path={path}>
      <FixedTab label="All Links">
        <LinkList
          links={links}
          totalCount={linksTotalCount}
          onRequestFetch={handleRequestFetch}
          onClickRow={handleRowClick}
          onClickDeleteButton={handleDeleteClick}
        />
      </FixedTab>
      <FixedTab label="Create a Link" uri="create">
        <LinkView onCreated={handleCreate} onError={handleError} />
      </FixedTab>
      <VariableTab<number>
        data={editingLinkIds}
        label={getTabLabel}
        uri={getTabUri}
        onClose={handleTabClose}
        keyExtractor={keyExtractor}
        render={variableTabRenderer}
      />
    </RouteTabs>
  );
};

export default Link;
